import type { CreateMarketplaceRequestBody } from "@assetwatch/marketplace-api";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { createMarketplaceRequest } from "../clients/marketplaceClient";

/**
 * Sends a Request More Information or Expand Coverage request. The modal shows
 * success and failure inline, so this keeps no toast side effects.
 *
 * `submit` ignores repeat calls while a request is in flight. `isPending` only
 * updates on the next render, so a fast double click could otherwise create two
 * Salesforce opportunities.
 */
export function useCreateMarketplaceRequest() {
  const inFlight = useRef(false);
  const mutation = useMutation({
    mutationFn: createMarketplaceRequest,
    onSettled: () => {
      inFlight.current = false;
    },
  });

  const submit = (body: CreateMarketplaceRequestBody) => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate(body);
  };

  const reset = () => {
    inFlight.current = false;
    mutation.reset();
  };

  return { ...mutation, submit, reset };
}
